import {
  DateTimeStyles,
  DateStyles,
  Formats,
  Configs,
  NumberStyles,
  RelativeStyles,
  TimeZoneNames,
  UnitStyles,
  UnitTypes,
  LanguageSwitcherParentTypes,
  Language
} from './types';
import { DisplayNameTypes } from './types/styles';

const numberStyles: NumberStyles = {
  currency: 'currency',
  unit: 'unit'
};
export const currencyCodeDefault: string = 'USD';
const dateTimeStyles: DateTimeStyles = {
  full: 'full',
  long: 'long',
  medium: 'medium',
  short: 'short'
};
const timeZoneNames: TimeZoneNames = {
  long: 'long',
  short: 'short',
  medium: 'medium'
};
const unitStyles: UnitStyles = {
  long: 'long'
};
export const unitTypes: UnitTypes = {
  day: 'day',
  kilometer: 'kilometer',
  mile: 'mile',
  month: 'month',
  week: 'week',
  year: 'year'
};
export const displayNameTypes: DisplayNameTypes = {
  language: 'language',
  region: 'region',
  script: 'script',
  currency: 'currency'
};

export const en: string = Language.En;
export const fr: string = Language.Fr;
export const de: string = Language.De;
export const es: string = Language.Es;
export const pt: string = Language.Pt;
export const it: string = Language.It;
export const nl: string = Language.Nl;
export const ro: string = Language.Ro;

export const enUS: string = 'en-US';
export const enCA: string = 'en-CA';
export const enGB: string = 'en-GB';
export const enAU: string = 'en-AU';
export const frFR: string = 'fr-FR';
export const frCA: string = 'fr-CA';
export const frBE: string = 'fr-BE';
export const frCH: string = 'fr-CH';
export const deDE: string = 'de-DE';
export const deAT: string = 'de-AT';
export const esES: string = 'es-ES';
export const esMX: string = 'es-MX';
export const esAR: string = 'es-AR';
export const ptPT: string = 'pt-PT';
export const ptBR: string = 'pt-BR';
export const itIT: string = 'it-IT';
export const itCH: string = 'it-CH';
export const nlNL: string = 'nl-NL';
export const nlBE: string = 'nl-BE';
export const roRO: string = 'ro-RO';
export const roMD: string = 'ro-MD';

export const defaultLocale = enUS;
export const defaultLanguage = Language.En;
export const localeCookie: string = '.culture';
export const timeZoneCookie: string = 'AionTimeZone';
export const isBrowserTimeZoneCookie: string = 'AionIsBrowserTimeZone';
export const mvcAppLocaleCookie: string = '.AspNetCore.Culture';
// fallback language must be the first in the group
export const supportedLocales: Array<string> = [enUS, enGB, enAU];

export const additionalSupportedLocales: Array<string> = [
  enCA,
  frFR,
  frCA,
  frBE,
  frCH,
  deDE,
  deAT,
  esES,
  esMX,
  esAR,
  ptPT,
  ptBR,
  itCH,
  itIT,
  nlBE,
  nlNL,
  roRO,
  roMD
];

export const supportedLanguages: Array<string> = [Language.En];

export const additionalSupportedLanguages: Array<string> = [
  Language.Fr,
  Language.De,
  Language.Es,
  Language.Pt,
  Language.It,
  Language.Nl,
  Language.Ro
];

export const languageOptions = [
  { value: enUS, languageCode: Language.En, countryCode: 'US' },
  { value: enGB, languageCode: Language.En, countryCode: 'GB' },
  { value: enAU, languageCode: Language.En, countryCode: 'AU' }
];

export const additionalLanguageOptions = [
  { value: enCA, languageCode: Language.En, countryCode: 'CA' },
  { value: frFR, languageCode: Language.Fr, countryCode: 'FR' },
  { value: frCA, languageCode: Language.Fr, countryCode: 'CA' },
  { value: frBE, languageCode: Language.Fr, countryCode: 'BE' },
  { value: frCH, languageCode: Language.Fr, countryCode: 'CH' },
  { value: deDE, languageCode: Language.De, countryCode: 'DE' },
  { value: deAT, languageCode: Language.De, countryCode: 'AT' },
  { value: esES, languageCode: Language.Es, countryCode: 'ES' },
  { value: esMX, languageCode: Language.Es, countryCode: 'MX' },
  { value: esAR, languageCode: Language.Es, countryCode: 'AR' },
  { value: ptPT, languageCode: Language.Pt, countryCode: 'PT' },
  { value: ptBR, languageCode: Language.Pt, countryCode: 'BR' },
  { value: itIT, languageCode: Language.It, countryCode: 'IT' },
  { value: itCH, languageCode: Language.It, countryCode: 'CH' },
  { value: nlNL, languageCode: Language.Nl, countryCode: 'NL' },
  { value: nlBE, languageCode: Language.Nl, countryCode: 'BE' },
  { value: roRO, languageCode: Language.Ro, countryCode: 'RO' },
  { value: roMD, languageCode: Language.Ro, countryCode: 'MD' }
];

export const languagesFallback = {
  en: enUS,
  mo: roRO,
  it: itIT,
  fr: frFR,
  de: deDE,
  es: esES,
  pt: ptPT,
  nl: nlNL,
  ro: roRO
};

export const kilometersInMile: number = 1.60934;
export const imperialLocales: Array<string> = [enUS, enGB];

export const relativeStyles: RelativeStyles = {
  long: 'long',
  short: 'short',
  narrow: 'narrow'
};

export const dateStyles: DateStyles = {
  month: {
    long: 'long',
    numeric: 'numeric'
  },
  year: {
    numeric: 'numeric'
  }
};

export const configs: Configs = {
  currency: {
    style: numberStyles.currency,
    currency: currencyCodeDefault
  },
  dateFull: {
    dateStyle: dateTimeStyles.full
  },
  dateLong: {
    dateStyle: dateTimeStyles.long
  },
  dateMedium: {
    dateStyle: dateTimeStyles.medium
  },
  dateShort: {
    dateStyle: dateTimeStyles.short
  },
  timeShort: {
    timeZoneName: timeZoneNames.short
  },
  timeMedium: {
    timeStyle: timeZoneNames.medium
  },
  relativeLong: { numeric: 'auto', style: relativeStyles.long },
  relativeNarrow: { numeric: 'auto', style: relativeStyles.narrow },
  relativeShort: { numeric: 'auto', style: relativeStyles.short },
  unit: {
    style: numberStyles.unit,
    unitDisplay: unitStyles.long
  }
};

export const formats: Formats = {
  currency: 'currency',
  dateCustom: 'dateCustom',
  dateFull: 'dateFull',
  dateLong: 'dateLong',
  dateMedium: 'dateMedium',
  dateShort: 'dateShort',
  distance: 'distance',
  number: 'number',
  timeShort: 'timeShort',
  timeMedium: 'timeMedium',
  relativeLong: 'relativeLong',
  relativeNarrow: 'relativeNarrow',
  relativeShort: 'relativeShort'
};

export const languageSwitcherParentTypes: LanguageSwitcherParentTypes = {
  header: 'header',
  footer: 'footer',
  registration: 'registration'
};

export const localizationFeatureFlag: string = 'AionLocalizationStatus';
